<template>
  <div
    class="c-input"
    :class="generatedClass"
  >
    <label
      v-if="label"
      :for="id"
      class="label"
    >
      <span>
        {{ label }}<span
          v-if="required"
          class="required"
        >*</span>
      </span>
    </label>

    <transition name="c-input-feedback">
      <span
        v-if="feedbackShow && feedbackMessage"
        class="feedback"
      >
        {{ feedbackMessage }}
      </span>
    </transition>

    <div class="inner">
      <c-icon
        class="icon"
        width="20px"
        height="20px"
        :icon="icon"
        v-if="icon && (type !== 'color')"
      />
      <c-icon
        class="icon"
        width="20px"
        height="20px"
        :icon="icon || 'color-sampler'"
        v-if="type === 'color'"
      />
      <textarea
        v-if="textArea"
        class="input"
        :id="id"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder"
        v-model="internalValue"
        v-bind="$attrs"
        autocomplete="off"
        @focus="active = true"
        @blur="active = false"
        @change="handleChange"
        @keyup="handleKeyup"
        @keyup.enter="handleEnter"
        @keyup.esc="handleEsc"
        @input="handleMaskInput"
      />

      <input
        v-else-if="(type !== 'file')"
        :class="{ '-empty': !internalValue }"
        class="input"
        ref="input"
        :id="id"
        :disabled="disabled"
        :required="required"
        autofill="new-password"
        :placeholder="placeholder"
        v-mask="mask"
        v-model="internalValue"
        v-money="isMoney ? moneyMask : false"
        autocomplete="off"
        @focus="active = true"
        @blur="active = false"
        @change="handleChange"
        @keyup="handleKeyup"
        @keyup.enter="handleEnter"
        @keyup.esc="handleEsc"
        @input="handleMaskInput"
      />

      <div
        class="input"
        v-else
      >
        <input
          @change="handleFile"
          :id="id"
          :disabled="disabled"
          :accept="fileAccept"
          autofill="new-password"
          :class="['input-field', { '-disabled': disabled }]"
          type="file"
        />
        <label
          :for="id"
          class="input-label"
        >
          <span v-if="fileName">
            <c-icon
              icon="files-compare"
              width="16"
            />
            <span>{{ fileName }}</span>
          </span>
          <span
            class="default"
            v-else
          >
            <c-icon
              icon="file-plus"
              width="16"
            />
            <span>{{ fileLabel }}</span>
          </span>
        </label>
      </div>

      <the-mask
        v-if="type === 'color'"
        class="input -color"
        ref="color"
        type="text"
        mask="#FFFFFF"
        :masked="true"
        :tokens="hexTokens"
        autofill="new-password"
        v-model="internalValue"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder"
        autocomplete="off"
        @focus="active = true"
        @blur="active = false"
        @change="handleChange"
        @keyup="handleKeyup"
        @keyup.enter="handleEnter"
        @keyup.esc="handleEsc"
        @input="handleMaskInput"
      />
    </div>
  </div>
</template>

<script>
import { mask, TheMask } from 'vue-the-mask'
import { VMoney } from 'v-money'

import shortenName from '@/helpers/shortenName'

export default {
  components: {
    TheMask
  },
  directives: {
    mask (el, binding) {
      if (!binding.value) {
        return
      }
      return mask(el, binding)
    },
    money: VMoney
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    isMoney: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: value => 'text|number|email|password|search|url|tel|file|color'.split('|').indexOf(value) > -1
    },
    fileLabel: {
      type: String,
      default: 'Adicionar arquivo'
    },
    fileAccept: {
      type: String,
      default: '*'
    },
    label: {
      type: String,
      default: ''
    },
    mask: {
      type: [Array, String],
      default: ''
    },
    moneyMask: {
      type: Object,
      default: () => ({
        prefix: 'R$ ',
        decimal: ',',
        thousands: ''
      })
    },
    icon: {
      type: String,
      default: ''
    },
    feedbackType: {
      type: String,
      default: 'error'
    },
    feedbackMessage: {
      type: String,
      default: ''
    },
    feedbackShow: {
      type: Boolean,
      default: false
    },
    textArea: {
      type: Boolean,
      default: false
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {
      default () { return [] }
    }
  },
  data () {
    return {
      id: -1,
      active: false,
      inputed: false,
      internalValue: null,
      fileName: false,
      hexTokens: {
        F: {
          pattern: /[0-9a-fA-F]/,
          transform: v => v.toLocaleUpperCase()
        }
      }
    }
  },
  computed: {
    generatedClass () {
      return {
        '-file': this.type === 'file',
        '-color': this.type === 'color',
        '-disabled': this.disabled,
        '-active': this.active,
        '-inputed': this.inputed,
        '-hasIcon': this.icon,
        '-success': this.feedbackType === 'success',
        '-error': this.feedbackType === 'error',
        '-showFeedback': this.feedbackShow,
        '-textarea': this.textArea
      }
    }
  },
  watch: {
    internalValue: function (val) {
      this.inputed = val.length > 0
    },
    value: {
      handler (v) {
        if (v) {
          this.internalValue = v.toString()
        } else { this.internalValue = '' }
      },
      immediate: true,
      deep: true
    }
  },
  created () {
    this.inputed = !!this.internalValue
    this.id = `${this._uid}`
  },
  mounted () {
    if (!this.textArea && this.$refs.input) {
      this.$refs.input.type = this.type
    }
  },
  methods: {
    handleInput () { this.$emit('input', this.internalValue) },
    handleChange () { this.$emit('change', this.internalValue) },
    handleKeyup () { this.$emit('keyup', this.internalValue) },
    handleMaskInput () { this.$emit('input', this.internalValue) },
    handleEnter () { this.$emit('enter', this.internalValue) },
    handleFile ({ target }) {
      if (target.files.length) {
        this.$emit('input', target.files[0])
        this.fileName = shortenName(target.files[0].name, true)
      } else {
        this.fileName = false
      }
    },
    handleEsc () {
      this.active = false
      this.$el.querySelector('input').blur()
    }
  }
}
</script>

<style lang="scss">
@import "~@styles/convenia-components/reference.scss";

$c-input-background-color: #fdfdfd !default;
$c-input-color: #5e6684 !default;
$c-input-placeholder-color: #cfd2e0 !default;
$c-input-label-color: #8c92b1 !default;
$c-input-border-color: #bec1d4 !default;
$c-input-active-border-color: #8fb2f4 !default;
$c-input-disabled-background-color: #D8D8D8 !default;
$c-input-disabled-border-color: #BEC0D3!default;
$c-input-disabled-color: #bdc0d1 !default;
$c-input-success-border-color: darken(#59efb2, 5) !default;
$c-input-success-color: darken(#59eeb2, 20) !default;
$c-input-error-border-color: #ff7987 !default;
$c-input-error-color: #ff7986 !default;

$c-input-label-font-size: 10px !default;
$c-input-mobile-font-size: 16px !default;
$c-input-font-size: 13px !default;
$c-input-feedback-font-size: 13px !default;

$c-input-height: 40px;
$c-input-text-indent: 12px;

.c-input {
  position: relative;

  & > .label {
    color: $c-input-label-color;
    font-size: $c-input-label-font-size;
    text-transform: uppercase;
    margin-bottom: inner-base(0.5);
    display: block;
    line-height: 1;
  }
  & > .label .required {
    color: $c-input-error-color;
    display: inline-block;
    margin-left: 2px;
  }
  & > .feedback {
    font-size: $c-input-feedback-font-size;
    position: absolute;
    bottom: 3px;
    left: 0;
    right: 0;
    line-height: 1;
    z-index: 1;
  }
  & > .inner {
    @extend %c-input-inner;
    position: relative;
    z-index: 2;
  }
  &.-textarea > .inner {
    @extend %c-input-textarea-inner;
  }
  &.-color > .inner {
    display: flex;
    flex-flow: row wrap;
  }
  &.-showFeedback {
    padding-bottom: outer-base();
  }
  &.-showFeedback.-success {
    & > .inner {
      border-color: $c-input-success-border-color;
    }
    & > .feedback,
    & > .inner > .input {
      color: $c-input-success-color;
    }
    & > .inner > .icon {
      fill: $c-input-success-color;
    }
  }
  &.-showFeedback.-error {
    & > .inner {
      border-color: $c-input-error-border-color;
    }
    & > .feedback,
    & > .inner > .input {
      color: $c-input-error-color;
    }
    & > .inner > .icon {
      fill: $c-input-error-color;
    }
  }
  &.-disabled {
    &:hover {
      cursor: default;
      pointer-events: none;
    }

    & .input {
      background-color: $c-input-disabled-background-color !important;
      border-color: $c-input-disabled-border-color !important;

      color: $c-input-disabled-color;
      font-weight: 300;
      pointer-events: none;
    }
  }
  &.-active {
    & > .inner {
      transition: box-shadow 0.3s ease;
      box-shadow: 0 0 0 3px $c-input-active-border-color;
      border-color: transparent;
    }
  }
  &.-showFeedback.-active.-error {
    & > .inner {
      box-shadow: 0 0 0 3px $c-input-error-border-color;
    }
  }
  &.-showFeedback.-active.-success {
    & > .inner {
      box-shadow: 0 0 0 3px $c-input-success-border-color;
    }
  }
  &.-hasIcon .input {
    text-indent: calc(#{$c-input-text-indent * 2} + 18px) !important;
  }
  &.-file {
    .inner {
      border-style: dashed;
    }

    .input-field {
      display: none;
    }

    .input-label {
      display: block;
      width: 100%;
      height: 100%;
      text-align: center;
      font-size: 13px;
      text-transform: uppercase;
      color: color(link);
      fill: darken($c-input-placeholder-color, 10);
      pointer-events: all;
      transition: background 0.3s ease;
      will-change: background;
      cursor: pointer;
      line-height: $c-input-height;
      .c-icon {
        position: relative;
        top: 2px;
      }
      &:hover {
        background: darken(#fdfdfd, 2);
        .default span {
          text-decoration: underline;
        }
      }
    }
  }
  &.-color {
  }
}
%c-input-inner {
  height: $c-input-height;
  border: 1px solid;
  border-color: $c-input-border-color;
  background-color: $c-input-background-color;
  border-radius: 3px;

  & > .icon {
    position: absolute;
    top: 9px;
    left: $c-input-text-indent;
    fill: #bec1d4;
  }

  & > .input {
    background-color: transparent;
    height: 100%;
    width: 100%;
    border: none;
    font-size: $c-input-font-size;
    text-indent: $c-input-text-indent;
    color: $c-input-color;
    &::placeholder {
      color: $c-input-placeholder-color;
    }
    &:focus,
    &:active {
      outline: none;
    }

    &[type="color"] {
      padding-left: calc(#{$c-input-text-indent * 2} + 18px) !important;
      width: 80px;
      position: relative;

      &.-empty::after {
        content: "";
        position: absolute;
        height: 2px;
        background-color: red;
        width: 43px;
        top: 4px;
        left: 46px;
        transform-origin: 0 0;
        transform: rotate(41deg);
      }
    }
    &[type="color"] + input {
      width: auto;
      max-width: calc(100% - 80px);
    }

    @include medium-down {
      font-size: $c-input-mobile-font-size;
    }
  }
}
%c-input-textarea-inner {
  min-height: $c-input-height;
  height: auto;
  padding-left: $c-input-text-indent;
  font-size: 0;

  & > .input {
    min-height: calc(#{$c-input-height} - 2px);
    height: auto;
    resize: vertical;
    text-indent: 0;
  }
}
.c-input-feedback-enter-active,
.c-input-feedback-leave-active {
  transition: all 0.3s ease-out;
}
.c-input-feedback-enter,
.c-input-feedback-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>



// WEBPACK FOOTER //
// src/components/convenia-components/CInput.vue